import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronRight, ClipboardList, Hammer, Workflow as WorkflowIcon } from 'lucide-react'
import { useTranslation } from '@/i18n'
import AppSidebar from '@/components/AppSidebar'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import {
  IntuiAlertDialog,
  IntuiAlertDialogAction,
  IntuiAlertDialogCancel,
  IntuiAlertDialogContent,
  IntuiAlertDialogDescription,
  IntuiAlertDialogFooter,
  IntuiAlertDialogHeader,
  IntuiAlertDialogTitle,
} from '@/components/intueri/IntuiAlertDialog'
import { PAGE_HEADER } from '@/components/layout/pageHeader'
import { cn } from '@/lib/utils'
import WorkflowCanvas from '../builder/WorkflowCanvas'
import FloatingPanel from '../builder/FloatingPanel'
import type { WorkflowGraphNodeData } from '../workflowTypes'
import RunChat from './RunChat'
import RunReport from './RunReport'
import StepInspector from './StepInspector'
import { buildRunGraph, latestPlan, withLiveCards } from './runGraph'
import { useRunLiveCards } from './liveCards'
import { ownerPlan } from './runHistory'
import { continuesRun } from './runRules'
import { useWorkflowRun } from './useWorkflowRun'
import { withRun, withView, type View } from './runUrl'
import { ERROR_BAR, HINT_BAR, isAvailabilityHint, unavailableText } from './unavailableText'

/** Statuses in which the engine is at work and the first node's pill reads "Running…". */
const RUNNING = new Set(['understanding', 'planning', 'running', 'parked'])

/**
 * The old workflow builder over a v4 run (`/workflow/new?runId=`): the breadcrumb header with the
 * Build | Report tabs, the step canvas drawn from the run's plan, and the floating chat. The first
 * node's "Run Workflow" pill is the plan's approval; "Stop Workflow" cancels the run. Everything the
 * page shows comes from the engine; nothing is authored or saved here.
 */
export default function RunBuilder() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const runId = searchParams.get('runId')
  const view: View = searchParams.get('view') === 'report' ? 'report' : 'build'
  const wf = useWorkflowRun(runId)
  const run = wf.run
  // The plan step whose card was clicked; the docked inspector shows it. A new run drops it.
  const [selected, setSelected] = useState<string | null>(null)
  useEffect(() => setSelected(null), [runId])
  // Stop and Cancel ask first, as the old builder did: a cancelled run cannot be resumed.
  const [confirmStop, setConfirmStop] = useState(false)
  const plan = latestPlan(run)
  // One instance for the run: the canvas draws the cards, the chat's steps add them.
  const live = useRunLiveCards(run?.runId ?? null)

  const awaitingApproval = run?.status === 'awaiting_approval'
  const running = Boolean(run && RUNNING.has(run.status))
  const finished = run?.status === 'finished'
  const planning = Boolean(run && (run.status === 'understanding' || run.status === 'planning'))

  const graph = useMemo(() => {
    const { nodes, edges } = buildRunGraph(run)
    if (nodes.length === 0) return { nodes, edges }
    const [first, ...rest] = nodes
    let data: WorkflowGraphNodeData = first.data
    if (awaitingApproval)
      data = {
        ...data,
        onRun: () => void wf.approve(),
        ...(run && continuesRun(run)
          ? { runLabel: t('workflow:builder.run_continue', { defaultValue: 'Continue with v{{version}}', version: run.planVersion }) }
          : {}),
      }
    else if (running) data = { ...data, running: true, onStop: () => setConfirmStop(true), stopping: wf.busy }
    return { nodes: [{ ...first, data }, ...rest], edges }
    // approve/cancel are stable hook callbacks; busy drives the stopping spinner.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, awaitingApproval, running, wf.busy, t])

  const canvas = useMemo(() => withLiveCards(graph, live.cards, live.remove), [graph, live.cards, live.remove])
  // Adding a card refits the view so the card is in it; removing one, or a status refresh, leaves
  // the user's pan and zoom alone.
  const seenCards = useRef(live.cards.length)
  const cardEpoch = useRef(0)
  if (live.cards.length > seenCards.current) cardEpoch.current += 1
  seenCards.current = live.cards.length

  // A card from an earlier version opens with that version's plan; only the latest awaiting approval is editable.
  const inspected = selected && run ? ownerPlan(run, selected) : null

  const title = run?.question ?? t('workflow:builder.untitled', { defaultValue: 'Untitled Workflow' })
  const setView = (next: View) => setSearchParams(withView(searchParams, next), { replace: true })
  const follow = (id: string | null) => {
    if (id) setSearchParams(withRun(searchParams, id))
  }

  return (
    <div className="flex h-screen">
      <AppSidebar />
      <div className="flex flex-1 flex-col min-w-0">
        <header className={`${PAGE_HEADER} gap-3`}>
          <div className="flex min-w-0 flex-1 items-center gap-1.5 text-sm">
            <button
              type="button"
              onClick={() => navigate('/workflow')}
              className="flex shrink-0 items-center gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <WorkflowIcon className="size-4" />
              {t('nav:menu.workflow', { defaultValue: 'Workflow' })}
            </button>
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
            {/* The question is the run's name; the engine owns it, so it is plain text. */}
            <span
              className="truncate px-1.5 py-0.5 text-sm font-medium text-foreground max-w-[clamp(120px,28vw,320px)]"
              title={title}
            >
              {title}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {run && (
              <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-muted/60 p-1 dark:bg-dark-muted/60">
                {(
                  [
                    ['build', Hammer, t('workflow:builder.tab_build', { defaultValue: 'Build' })],
                    ['report', ClipboardList, t('workflow:builder.tab_report', { defaultValue: 'Report' })],
                  ] as const
                ).map(([value, Icon, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setView(value)}
                    className={cn(
                      'flex items-center gap-1.5 rounded-md px-3 py-1 text-sm font-medium transition-colors',
                      view === value
                        ? 'bg-background text-foreground shadow-sm dark:bg-dark-card'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <Icon className="size-4" />
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-1 items-center justify-end gap-2">
            {run && !finished && (
              <IntuiButton variant="outline" size="lg" disabled={wf.busy} onClick={() => setConfirmStop(true)}>
                {t('workflow:builder.cancel_run', { defaultValue: 'Cancel' })}
              </IntuiButton>
            )}
          </div>
        </header>

        {wf.unavailable && (
          <div
            role={isAvailabilityHint(wf.unavailable) ? 'status' : 'alert'}
            className={isAvailabilityHint(wf.unavailable) ? HINT_BAR : ERROR_BAR}
          >
            {unavailableText(wf.unavailable, t)}
          </div>
        )}

        {view === 'report' ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            {run ? (
              <RunReport run={run} busy={wf.busy} onRerun={(period) => wf.rerun(period).then(follow)} />
            ) : (
              <p className="p-6 text-sm text-muted-foreground">
                {t('workflow:builder.report_pending', {
                  defaultValue: 'The answer appears here when the run finishes.',
                })}
              </p>
            )}
          </div>
        ) : (
          <div className="flex min-h-0 flex-1">
            <div className="relative min-w-0 flex-1">
              <WorkflowCanvas
                nodes={canvas.nodes}
                edges={canvas.edges}
                // The view is fitted when the graph itself changes (a plan arrives, a re-plan), not on
                // the run's refreshes, which redraw the same steps with new statuses.
                fitKey={plan ? `${plan.planId}:${plan.version}:${graph.nodes.length}:${cardEpoch.current}` : 'none'}
                runningGlow={run?.status === 'running'}
                generatingLabel={
                  wf.loading
                    ? t('workflow:builder.loading_run', { defaultValue: 'Loading the run…' })
                    : planning && graph.nodes.length === 0
                      ? t('workflow:builder.planning', { defaultValue: 'Planning…' })
                      : undefined
                }
                generatingBodyLabel={t('workflow:builder.planning_body', {
                  defaultValue: 'The engine is working out the plan',
                })}
                emptyState={
                  wf.notFound ? (
                    // The API has no such run here: another organization's link, or a run that is gone.
                    <div className="rounded-xl border border-border bg-background px-10 py-6 text-center shadow-sm dark:bg-dark-card">
                      <p className="text-sm font-semibold text-foreground">
                        {t('workflow:builder.run_not_found_title', { defaultValue: 'This run is not here' })}
                      </p>
                      <p className="mt-1 max-w-[44ch] text-sm text-muted-foreground">
                        {t('workflow:builder.run_not_found', {
                          defaultValue: 'It belongs to another organization, or it no longer exists. Switch organization, or start a new workflow.',
                        })}
                      </p>
                      <IntuiButton size="sm" variant="outline" className="pointer-events-auto mt-3" onClick={() => navigate('/workflow')}>
                        {t('workflow:builder.back_to_library', { defaultValue: 'Back to workflows' })}
                      </IntuiButton>
                    </div>
                  ) : !runId ? (
                    <div className="rounded-xl border border-border bg-background px-10 py-6 text-center shadow-sm dark:bg-dark-card">
                      <p className="text-sm font-semibold text-foreground">
                        {t('workflow:builder.empty_title', { defaultValue: "Let's build your workflow" })}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {t('workflow:builder.empty_hint_run', {
                          defaultValue: 'Ask a question in the chat; the engine plans the workflow and waits for your approval.',
                        })}
                      </p>
                    </div>
                  ) : undefined
                }
                onNodeClick={(data) => {
                  const d = data as WorkflowGraphNodeData
                  if (d.kind === 'step' && d.stepId) setSelected(d.stepId)
                }}
              />
              {/* The docked inspector owns the right side while it is open; the chat comes back as it was. */}
              <FloatingPanel title={run?.question} hidden={Boolean(selected && inspected) || wf.notFound}>
                <RunChat
                  wf={wf}
                  onStarted={follow}
                  onViewReport={() => setView('report')}
                  onSelectStep={setSelected}
                  onRerun={(period) => wf.rerun(period).then(follow)}
                  liveCards={live.cards}
                  onAddLiveCard={live.add}
                />
              </FloatingPanel>
            </div>
            {selected && run && inspected && (
              <aside className="flex w-[400px] shrink-0 flex-col border-l border-border bg-background dark:bg-dark-card">
                <StepInspector
                  run={run}
                  plan={inspected}
                  stepId={selected}
                  onClose={() => setSelected(null)}
                  editable={awaitingApproval && inspected === plan}
                  busy={wf.busy}
                  error={wf.error}
                  onSave={wf.editPlan}
                />
              </aside>
            )}
          </div>
        )}
      </div>
      <IntuiAlertDialog open={confirmStop} onOpenChange={setConfirmStop}>
        <IntuiAlertDialogContent>
          <IntuiAlertDialogHeader>
            <IntuiAlertDialogTitle>
              {t('workflow:builder.stop_confirm_title', { defaultValue: 'Stop this workflow run?' })}
            </IntuiAlertDialogTitle>
            <IntuiAlertDialogDescription>
              {t('workflow:builder.stop_confirm_description', {
                defaultValue:
                  'The run will stop after the current step finishes and cannot be resumed. You can start a new run at any time.',
              })}
            </IntuiAlertDialogDescription>
          </IntuiAlertDialogHeader>
          <IntuiAlertDialogFooter>
            <IntuiAlertDialogCancel>{t('workflow:builder.stop_confirm_keep', { defaultValue: 'Keep running' })}</IntuiAlertDialogCancel>
            <IntuiAlertDialogAction onClick={() => void wf.cancel()}>
              {t('workflow:builder.stop', { defaultValue: 'Stop Workflow' })}
            </IntuiAlertDialogAction>
          </IntuiAlertDialogFooter>
        </IntuiAlertDialogContent>
      </IntuiAlertDialog>
    </div>
  )
}
